import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

interface CooldownTimerProps {
  isLocked: boolean;
  timeRemaining: string;
  onLockChange?: (locked: boolean) => void;
  animateOnUnlock?: boolean;
}

/**
 * CooldownTimer Component
 * Displays a visible, animated cooldown timer for feature usage
 * Shows remaining time in HH:MM:SS format
 * Animates when timer completes (unlocks)
 */
const CooldownTimer: React.FC<CooldownTimerProps> = ({
  isLocked,
  timeRemaining,
  onLockChange,
  animateOnUnlock = true
}) => {
  const [wasJustUnlocked, setWasJustUnlocked] = useState(false);
  const [prevLocked, setPrevLocked] = useState(isLocked);

  // Detect when timer completes (transitions from locked to unlocked)
  useEffect(() => {
    if (prevLocked && !isLocked) {
      setWasJustUnlocked(true);
      // Clear animation after 2 seconds
      const timeout = setTimeout(() => setWasJustUnlocked(false), 2000);
      return () => clearTimeout(timeout);
    }
    setPrevLocked(isLocked);
  }, [isLocked, prevLocked]);

  // Call parent callback when lock state changes
  useEffect(() => {
    if (onLockChange) {
      onLockChange(isLocked);
    }
  }, [isLocked, onLockChange]);

  if (!isLocked && !wasJustUnlocked) {
    return null;
  }

  return (
    <div
      className={`
        flex flex-col items-center justify-center gap-2 px-6 py-3 rounded-xl font-mono text-lg
        transition-all duration-300 w-full max-w-sm mx-auto
        ${
          wasJustUnlocked
            ? 'bg-gradient-to-r from-purple-500 via-purple-600 to-purple-700 text-white shadow-lg shadow-purple-500/30 animate-pulse'
            : 'bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 text-purple-100 shadow-lg shadow-purple-600/40'
        }
      `}
    >
      {isLocked ? (
        <>
          <div className="flex items-center gap-3">
            <Clock size={20} className="flex-shrink-0 animate-spin" />
            <span className="font-bold tracking-wider">{timeRemaining}</span>
          </div>
          <span className="text-xs text-purple-200 opacity-80">Next available in {timeRemaining}</span>
        </>
      ) : (
        <>
          <Clock size={20} className="flex-shrink-0" />
          <span className="font-bold text-xl">Ready!</span>
        </>
      )}
    </div>
  );
};

export default CooldownTimer;
